import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import PreviewDialog from "./PreviewDialog";
import {
  Input,
  makeStyles,
  Field,
  Textarea,
  Dropdown,
  Option,
  TagPicker,
  TagPickerInput,
  TagPickerControl,
  TagPickerGroup,
  Tag,
  InfoLabel
} from "@fluentui/react-components";
import { useAuth } from "../contexts/AuthContext";
import { useApi } from "../contexts/ApiContext";
import configs from "../../configs";
import { resolvePublishingCategoryId, resolvePublishingCategoryName } from "./publishCategorySelection";

const useStyles = makeStyles({
  field: {
    "& label": {
      color: "var(--Gray-3, #828282)"
    }
  }
});

function PublishDialog({
  thumbnailUrl,
  initialName,
  initialDescription,
  initialTags,
  initialCategory,
  initialAuthor,
  platformSiteSettings,
  categoriesData,
  onConfirm,
  onCancel
}) {
  const styles = useStyles();
  const { authUser } = useAuth();
  const api = useApi();
  const isAdmin = api.hasPermission(configs.ADMIN_PERMISSION);
  const [name, setName] = useState(initialName);
  const [error, setError] = useState("");
  const [disable, setDisable] = useState(false);
  const [description, setDescription] = useState(initialDescription || "");
  const [selectedTags, setSelectedTags] = useState(initialTags || []);
  const [tagInputValue, setTagInputValue] = useState("");
  const platformAuthorName = platformSiteSettings?.branding?.identity?.name || "Platform Brand";

  // Convert initialAuthor name to key ("user" or "platform")
  const getAuthorKey = useCallback(() => {
    if (!initialAuthor) return "user";

    if (initialAuthor === platformAuthorName) {
      return "platform";
    }

    // Check if initialAuthor matches user name
    if (authUser && initialAuthor === authUser.name) {
      return "user";
    }

    // Default to user if no match
    return "user";
  }, [initialAuthor, platformAuthorName, authUser]);

  const [selectedAuthor, setSelectedAuthor] = useState(getAuthorKey());

  // Process categories data from props
  const categories = React.useMemo(() => {
    if (categoriesData && categoriesData.data && Array.isArray(categoriesData.data)) {
      return categoriesData.data.filter(cat => cat.isActive !== false);
    }
    return [];
  }, [categoriesData]);

  const categoriesEnabled = React.useMemo(() => {
    return categories.length > 0 && categoriesData?.enabled;
  }, [categories, categoriesData]);

  const [selectedCategory, setSelectedCategory] = useState(() =>
    resolvePublishingCategoryId(initialCategory, categories)
  );

  const onChangeName = _ev => {
    let disable = false;
    const newName = _ev.target.value;

    if (newName.length < 5) {
      setError("Name must be at least 5 characters long");
      disable = true;
    } else {
      setError("");
    }

    const regex = /^[A-Za-z0-9-':"(),.~ ]*$/;
    if (!regex.test(newName)) {
      setError("Name must be between 5 and 64 characters and cannot contain underscores or special characters");
      disable = true;
    } else if (!disable) {
      setError("");
    }

    setDisable(disable);

    if (newName.length <= 64) {
      setName(newName);
    }
  };

  const onChangeDescription = _ev => {
    setDescription(_ev.target.value);
  };

  const onTagSelect = (_, data) => {
    setSelectedTags(data.selectedOptions);
  };

  const handleTagInputChange = event => {
    setTagInputValue(event.currentTarget.value);
  };

  const handleTagKeyDown = event => {
    if (event.key === "Enter" && tagInputValue.trim()) {
      event.preventDefault();
      setTagInputValue("");
      setSelectedTags(curr => (curr.includes(tagInputValue.trim()) ? curr : [...curr, tagInputValue.trim()]));
    }
  };

  const onConfirmCallback = e => {
    e.preventDefault();

    if (name.length < 5) {
      setError("Name must be at least 5 characters long");
      return;
    }

    const regex = /^[A-Za-z0-9-':"(),.~ ]*$/;
    if (!regex.test(name)) {
      setError("Name must be between 5 and 64 characters and cannot contain underscores or special characters");
      return;
    }

    // Get the actual author name instead of the key
    const authorName = selectedAuthor === "platform" ? platformAuthorName : authUser?.name || "User Name";

    onConfirm({
      name,
      description,
      selectedCategory: resolvePublishingCategoryName(selectedCategory, categories),
      selectedCategoryId: categoriesEnabled
        ? resolvePublishingCategoryId(selectedCategory, categories) || undefined
        : undefined,
      tags: selectedTags,
      author: authorName
    });
    setName("");
    setDescription("");
    setSelectedCategory("");
    setSelectedTags([]);
    setSelectedAuthor("user");
    setError("");
    setDisable(false);
  };

  const onCancelCallback = useCallback(
    e => {
      e.preventDefault();
      onCancel();
    },
    [onCancel]
  );

  return (
    <PreviewDialog
      imageSrc={thumbnailUrl}
      title="Publish"
      onConfirm={onConfirmCallback}
      onCancel={onCancelCallback}
      confirmLabel="Publish"
      disabled={disable}
    >
      <Field
        label="Name"
        required
        className={styles.field}
        validationState={error ? "error" : "none"}
        validationMessage={error}
      >
        <Input
          type="text"
          appearance="filled-darker"
          id="name"
          required
          pattern={"[A-Za-z0-9-':\"(),.~ ]{4,64}"}
          title="Name must be between 4 and 64 characters and cannot contain underscores"
          value={name}
          onChange={onChangeName}
        />
      </Field>

      {isAdmin && (
        <Field label="Author" required className={styles.field}>
          <Dropdown
            appearance="filled-darker"
            placeholder="Select author"
            value={selectedAuthor === "user" ? authUser?.name || "User Name" : platformAuthorName}
            selectedOptions={[selectedAuthor]}
            onOptionSelect={(e, data) => setSelectedAuthor(data.optionValue)}
            required
          >
            <Option value="user">{authUser?.name || "User Name"}</Option>
            {platformSiteSettings && <Option value="platform">{platformAuthorName}</Option>}
          </Dropdown>
        </Field>
      )}

      <Field label="Description" className={styles.field}>
        <Textarea
          appearance="filled-darker"
          id="description"
          rows={3}
          maxLength={255}
          value={description}
          onChange={onChangeDescription}
        />
      </Field>

      {/* Tag Picker used when searching only */}
      <Field
        label={{
          children: (_, slotProps) => (
            <InfoLabel {...slotProps} info="Add custom tags to help others discover your verse when searching.">
              Tags
            </InfoLabel>
          )
        }}
        className={styles.field}
      >
        <TagPicker noPopover onOptionSelect={onTagSelect} selectedOptions={selectedTags} appearance="filled-darker">
          <TagPickerControl>
            <TagPickerGroup aria-label="Selected Tags">
              {selectedTags.map((tag, index) => (
                <Tag key={index} shape="rounded" value={tag}>
                  {tag}
                </Tag>
              ))}
            </TagPickerGroup>
            <TagPickerInput
              value={tagInputValue}
              onChange={handleTagInputChange}
              onKeyDown={handleTagKeyDown}
              aria-label="Add Tags"
              placeholder="Type and press Enter to add tags"
            />
          </TagPickerControl>
        </TagPicker>
      </Field>

      {/* Category - Only show if enabled and categories exist, used with a filter or when searching */}
      {categoriesEnabled && categories.length > 0 && (
        <Field
          label={{
            children: (_, slotProps) => (
              <InfoLabel {...slotProps} info="Select a category to help users filter and find your verse more easily.">
                Category
              </InfoLabel>
            )
          }}
          className={styles.field}
        >
          <Dropdown
            appearance="filled-darker"
            placeholder="Select a category"
            value={resolvePublishingCategoryName(selectedCategory, categories)}
            onOptionSelect={(e, data) => setSelectedCategory(resolvePublishingCategoryId(data.optionValue, categories))}
          >
            <Option key="none" value="">
              None
            </Option>
            {categories.map(category => (
              <Option key={category.id} value={category.id}>
                {category.name}
              </Option>
            ))}
          </Dropdown>
        </Field>
      )}
    </PreviewDialog>
  );
}

PublishDialog.propTypes = {
  thumbnailUrl: PropTypes.string.isRequired,
  initialName: PropTypes.string.isRequired,
  initialDescription: PropTypes.string,
  initialTags: PropTypes.arrayOf(PropTypes.string),
  initialCategory: PropTypes.string,
  initialAuthor: PropTypes.string,
  platformSiteSettings: PropTypes.object,
  categoriesData: PropTypes.shape({
    data: PropTypes.array,
    enabled: PropTypes.bool
  }),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default PublishDialog;
